import React, { ChangeEvent, useContext, useEffect, useState, useRef } from 'react';

import { ArrowUturnLeftIcon, CheckCircleIcon, PencilIcon, PlusIcon, TrashIcon, XCircleIcon, } from '@heroicons/react/24/outline';
import Swal from 'sweetalert2';


import { AuthContext } from "../contexts/auth";
import { addEmployeeContract, getCCNLList, getContractTypesList, getEmployeeContractsList, updateEmployeeContract, deleteEmployeeContract } from '../services/api_contracts_employee';
import { getEmployeeWeeklyWorkingHour } from '../services/api_working_hours';
import { getWorkTaskList, getEntePrevidenzialeList, getMandatoryHiringTypesList, getClassificationLevelList, getWorkingHourTypesList } from '../services/api_resources';

import ListBox from './list_box';
import ComboboxAutocompleteAPI from './combobox_autocomplete_api';
import { estraiMessaggiErrore } from '../services/utils';

const getShiftTypeDescriptionByValue = function (shiftType, def = null) {
    const shiftTypeDescriptions = {
        fisso: 'Fisso',
        turno: 'Turno',
        a_chiamata: 'A Chiamata',
        senza_calendario: 'Senza Calendario',
    };

    return shiftTypeDescriptions[shiftType] || def;
};


const labels = {
    start_date: 'Data Inizio',
    end_date: 'Data Fine',
    shift_type: 'Tipologia Turno',
    week_hours: 'Ore Settimanali',
    typology: 'Tipologia Contrattuale',
    ccnl: 'CCNL',
    qualification: 'Qualifica Professionale',
    classification_level: 'Livello Inquadramento',
    working_hours_type: 'Tipo Orario',
    ral: 'Retribuzione (RAL)',
    social_security_institution: 'Ente Previdenziale',
    pat_inail: 'PAT Inail',
    is_working_partner: 'Socio Lavoratore',
    is_mobile_worker: 'Lavoratore in Mobilità',
    is_seasonal_work: 'Lavoro Stagionale',
    is_agriculture: 'Lavoro in Agricoltura',    
};

function formatDateToDisplay(date) {
    const d = new Date(date);
    if (isNaN(d)) return '';
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}


function ContractForm({employeeId, initialValues, successCallback, undoCallback, setUnsavedChanges}) {
    const { token } = useContext(AuthContext);
    const defaultValues = {
        start_date: '',
        end_date: '',
        shift_type: 'fisso',
        week_hours: '',
        typology: '',
        ccnl: '',
        qualification: '',
        classification_level: '',
        working_hours_type: '',
        ral: '',
        social_security_institution: '',
        pat_inail: '',
        is_working_partner: '',
        is_mobile_worker: '',
        is_seasonal_work: '',
        is_agriculture: '',
    };
    const [formData, setFormData] = useState(() => ({
        ...defaultValues,
        ...initialValues,
        start_date: initialValues?.start_date ? formatDateToDisplay(initialValues.start_date) : '',
        end_date: initialValues?.end_date ? formatDateToDisplay(initialValues.end_date) : '',
    }));
    const [fieldErrors, setFieldErrors] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [classificationLevels, setClassificationLevels] = useState([]);
    const [selectedCcnlId, setSelectedCcnlId] = useState(null); 
    const [selectedClassificationLevelId, setSelectedClassificationLevelId] = useState(null);
    const [formValues, setFormValues] = useState(initialValues);
    const [initialValuesCcnl, setInitialValuesCcnl] = useState({ ccnl: {}, classification_level: null });
    const startDateRef = useRef(null);
    const endDateRef = useRef(null);
    const ralRef = useRef(null);
    const patInailRef = useRef(null);
    const weekHoursRef = useRef(null);
    const [wwhEmployee, setWwhEmployee] = useState(null);

    
    useEffect(() => {
        if (initialValues && initialValues.ccnl) {
            setSelectedCcnlId(initialValues.ccnl.id);
        }
    }, [initialValues]);

    useEffect(() => {
        if (!selectedCcnlId) {
            setClassificationLevels([]);
            const classification_level = initialValues?.classification_level || null
            setFormData(prevState => ({
                ...prevState,
                classification_level: classification_level
            }));
            

            setSelectedClassificationLevelId(classification_level);
            return;
        }
    
        if (initialValuesCcnl.ccnl?.id === selectedCcnlId && initialValuesCcnl.classification_level) {
            setClassificationLevels(prevLevels => prevLevels);
            setFormData(prevState => ({
                ...prevState,
                classification_level: initialValuesCcnl.classification_level
            }));
            setSelectedClassificationLevelId(initialValuesCcnl.classification_level?.id || null);          
            
        } else {
            getClassificationLevelList(token, selectedCcnlId).then((data) => {
                setClassificationLevels(data);
                const classification_level = initialValues?.classification_level || null
                setFormData(prevState => ({
                    ...prevState,
                    classification_level: classification_level
                }));
                setSelectedClassificationLevelId(classification_level);
            });
        }
    }, [selectedCcnlId, token, initialValuesCcnl]);

    useEffect(() => {
        const fetchEmployeeWeeklyWorkingHourData = async () => {
            try {
                const employeeWeeklyWorkingHourData = await getEmployeeWeeklyWorkingHour(token, employeeId, 'shifts');
                if (employeeWeeklyWorkingHourData && employeeWeeklyWorkingHourData.weekly_working_hour) {
                    setWwhEmployee(employeeWeeklyWorkingHourData.weekly_working_hour || []);
                }
            } catch (error) {
                console.error('Errore durante il recupero dell\'orario lavorativo', error);
            }
        };
        fetchEmployeeWeeklyWorkingHourData();
    }, [token, employeeId]);

    const requiredFields = [
        'start_date',
        'shift_type',
    ];

    const isValidDate = (dateString) => {
        const regex = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/(\d{4})$/;
        if (!regex.test(dateString)) return false;
    
        const [day, month, year] = dateString.split('/').map(Number);
        const date = new Date(year, month - 1, day);
        
        return date.getFullYear() === year && date.getMonth() + 1 === month && date.getDate() === day;
    };
    
    const saveHandler = function () {
        let error = false;
        setIsSaving(true);
        
        requiredFields.forEach(requiredField => {
            if (!formData[requiredField]) {
                error = true;
                setFieldErrors(currentValue => ({
                    ...currentValue,
                    [requiredField]: 'Campo Obbligatorio'
                }));
            }
        });
    
        if (formData.start_date) {
            if (!isValidDate(formData.start_date)) {
                error = true;
                setFieldErrors(currentValue => ({
                    ...currentValue,
                    start_date: 'Formato non valido, usa ggmmaaaa'
                }));
            }
        }
    
        if (formData.end_date) {
            if (!isValidDate(formData.end_date)) {
                error = true;
                setFieldErrors(currentValue => ({
                    ...currentValue,
                    end_date: 'Formato non valido, usa ggmmaaaa'
                }));
            }
        }

        if (formData.week_hours) {
            const weekHoursRegex = /^\d+(\.\d{0,2})?$/;
            if (!weekHoursRegex.test(formData.week_hours)) {
                error = true;
                setFieldErrors(currentValue => ({
                    ...currentValue,
                    week_hours: 'Formato non valido, usa numeri con massimo due cifre decimali'
                }));
            }
        }

        if ((formData.week_hours === null || formData.week_hours === '' || formData.week_hours === undefined) && wwhEmployee) {
            error = true;
            setFieldErrors(currentValue => ({
                ...currentValue,
                week_hours: 'Non è possibile salvare un valore vuoto perchè è presente un turno lavorativo impostato.'
            }));
        }

        if (formData.ral && !validateRal(formData.ral)) {
            error = true;
            setFieldErrors(currentValue => ({
                ...currentValue,
                ral: 'Formato non valido, usa numeri con massimo due cifre decimali'
            }));
        }
    
        if (formData.start_date && formData.end_date) {
            const [startDay, startMonth, startYear] = formData.start_date.split('/');
            const [endDay, endMonth, endYear] = formData.end_date.split('/');
            
            const startDate = new Date(`${startYear}-${startMonth}-${startDay}`);
            const endDate = new Date(`${endYear}-${endMonth}-${endDay}`);
            
            if (endDate < startDate) {
                error = true;
                setFieldErrors(currentValue => ({
                    ...currentValue,
                    end_date: 'La data di fine deve essere successiva alla data di inizio'
                }));
            }
        }

        if (formData.pat_inail && (formData.pat_inail.length < 8 || formData.pat_inail.length > 10)) {
            error = true;
            setFieldErrors(currentValue => ({
                ...currentValue,
                pat_inail: 'Le cifre devono essere minimo 8 e massimo 10'
            }));
        }
        
        if (error) {
            setIsSaving(false);

            const fieldErrorsList = Object.keys(fieldErrors);
            if (fieldErrorsList.includes('start_date')) {
                startDateRef.current.scrollIntoView({ behavior: 'smooth' });
            } else if (fieldErrorsList.includes('end_date')) {
                endDateRef.current.scrollIntoView({ behavior: 'smooth' });
            } else if (fieldErrorsList.includes('week_hours')) {
                weekHoursRef.current.scrollIntoView({ behavior: 'smooth' });
            } else if (fieldErrorsList.includes('ral')) {
                ralRef.current.scrollIntoView({ behavior: 'smooth' });
            } else if (fieldErrorsList.includes('pat_inail')) {
                patInailRef.current.scrollIntoView({ behavior: 'smooth' });
            }
            return;
        }

       const formDataToSend = { ...formData };

        if (formDataToSend.end_date === '') {
            formDataToSend.end_date = null;
        }  else {
            const [day, month, year] = formDataToSend.end_date.split('/');
            formDataToSend.end_date = `${year}-${month}-${day}`;
        }

        const [startDay, startMonth, startYear] = formDataToSend.start_date.split('/');
        formDataToSend.start_date = `${startYear}-${startMonth}-${startDay}`;

        // for(const propName in formDataToSend) {
        //     const props = formDataToSend[propName];

        //     if(props && props.hasOwnProperty('id')) {
        //         formDataToSend[propName] = props.id;
        //     }
            
        //     if(!formDataToSend[propName]) {
        //         delete formDataToSend[propName];
        //     }

        // }

        Object.keys(formDataToSend).forEach(key => {
            if (formDataToSend[key] && formDataToSend[key].hasOwnProperty('id')) {
                formDataToSend[key] = formDataToSend[key].id;
            }
            
            if (formDataToSend[key] === '' || formDataToSend[key] === null || formDataToSend[key] === undefined) {
                formDataToSend[key] = null;
            }
        });

        if (initialValues && initialValues.id) {
            updateEmployeeContract(token, employeeId, initialValues.id, formDataToSend).then(
                res => {
                    Swal.fire({
                        title: "Contratto modificato con successo!",
                        icon: "success",
                    });
        
                    if(successCallback)
                        successCallback();
                        setIsSaving(false);
                        setUnsavedChanges(false);
                })
                .catch(error => {
                    setIsSaving(false);
        
                    if (error.response.data && typeof error.response.data === 'object') {
                        const errorMessages = error.response.data.non_field_errors;
                        
                        if (errorMessages && errorMessages[0] === "Il tuo abbonamento non consente l'inserimento di nuovi contratti attivi") {
                            Swal.fire({
                                icon: "error",
                                title: "Oops...",
                                html: `${errorMessages[0]}`,
                                showCancelButton: true,
                                confirmButtonText: 'Gestisci abbonamento',
                                cancelButtonText: 'Annulla',
                                cancelButtonColor: '#d1d5db',
                                allowOutsideClick: false,
                            }).then((result) => {
                                if (result.isConfirmed) {
                                    window.location.href = "/livelli";
                                }
                            });
                        } else {
                            const formattedErrors = Object.entries(error.response.data)
                                .map(([field, messages]) => {
                                    return messages.length > 0 ? `${field}: ${messages[0]}` : '';
                                })
                                .filter(message => message)
                                .join('\n');
                            
                            Swal.fire({
                                icon: "error",
                                title: "Oops...",
                                text: `Errore durante il salvataggio:\n${formattedErrors}`,
                            });
                        }
                    } else {
                        Swal.fire({
                            icon: "error",
                            title: "Oops...",
                            text: 'Errore durante il salvataggio. Controlla i dettagli.',
                        });
                    }
                });
            return;
        }

        addEmployeeContract(token, employeeId, formDataToSend)
        .then((res) => {
            Swal.fire({
                title: "Contratto creato con successo!",
                icon: "success",
            });
    
            if (successCallback) successCallback();
            setIsSaving(false);
            setUnsavedChanges(false);
        })
        .catch((error) => {
            console.error('Errore durante la creazione del contratto:', error);
    
            setIsSaving(false);
    
            let errorMessage = 'Si è verificato un errore durante il salvataggio. Per favore riprova.';
            if (error.response && error.response.data) {
                const errorData = error.response.data;
                const errorMessages = [];

                Object.keys(errorData).forEach((key) => {
                    const messages = errorData[key];
                    if (Array.isArray(messages)) {
                        errorMessages.push(...messages); 
                    } else if (typeof messages === 'string') {
                        errorMessages.push(messages);
                    }
                });
    
                if (errorMessages.length > 0) {
                    errorMessage = errorMessages.join(' ');
                }

                if (errorMessages.includes("Il tuo abbonamento non consente l'inserimento di nuovi contratti attivi")) {
                    Swal.fire({
                        icon: "error",
                        title: "Oops...",
                        html: "Il tuo abbonamento non consente l'inserimento di nuovi contratti attivi",
                        showCancelButton: true,
                        confirmButtonText: 'Gestisci abbonamento',
                        cancelButtonText: 'Annulla',
                        cancelButtonColor: '#d1d5db',
                        allowOutsideClick: false,
                    }).then((result) => {
                        if (result.isConfirmed) {
                            window.location.href = "/livelli";
                        }
                    });
                    return;
                }
            }
    
            Swal.fire({
                icon: "error",
                title: "Errore",
                text: errorMessage,
                confirmButtonText: "OK",
            });
        });    
    };

    const onChangeHandler = function (fieldName, event) {
        const value = event.target.value === '' ? null : event.target.value;
        
        setFieldErrors(currentValue => {
            delete currentValue[fieldName];

            return currentValue;
        })
        setFormData(
            (currentData) => {
                return {...currentData, [fieldName]: event.target.value};
            }
        );
        setUnsavedChanges(true);
    };

    const onListChangeHandler = function (fieldName, value) {
        setFieldErrors(currentValue => ({
            ...currentValue,
            [fieldName]: null,
        }));
    
        setFormData(currentData => ({
            ...currentData,
            [fieldName]: value !== null ? value : null,
        }));
    
        setUnsavedChanges(true);
    };   
    
    const handleComboBoxChange = (field, res) => {
        setFormData(prevState => {
            const newState = {
                ...prevState,
                [field]: res ? res.id : null,
            };
    
            if (field === 'ccnl' && !res) {
                newState.classification_level = null;
            }
    
            return newState;
        });

        if (field === 'ccnl') {
            setSelectedCcnlId(res ? res.id : null);
            if (!res) {
                setSelectedClassificationLevelId(null);
            }
        }
        setUnsavedChanges(true);
    };

    const handleChange = (field, e) => {
        let value = e.target.value;
        if (field === 'ral') {
            value = value.replace(',', '.');
            if (value.includes('.')) {
                const parts = value.split('.');
                value = parts[0] + '.' + parts[1].substring(0, 2);
            }
        } else if (field === 'pat_inail') {
            value = value.replace(/[^0-9]/g, '');
        }

        setFormData({
            ...formData,
            [field]: value
        });
        setUnsavedChanges(true);
    };

    const validateRal = (value) => {
        const regex = /^\d+(\.\d{1,2})?$/;
        return regex.test(value);
    };

    const formatDateInput = (value) => {
        const cleaned = value.replace(/\D/g, '');
        const day = cleaned.slice(0, 2);
        const month = cleaned.slice(2, 4);
        const year = cleaned.slice(4, 8);
        
        if (month.length > 0) {
            return `${day}/${month}${year.length > 0 ? '/' + year : ''}`;
        }
        return day;
    };
    
    const handleDateChange = (fieldName, event) => {
        const formattedValue = formatDateInput(event.target.value);
        onChangeHandler(fieldName, { target: { value: formattedValue } });
    };

    const fields = {
        start_date: (
            <input 
                value={formData['start_date'] || ''} 
                type="text" 
                required 
                className='shadow appearance-none border border-gray-300 rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline' 
                name="start_date" 
                onChange={(e) => handleDateChange('start_date', e)} 
                placeholder="ggmmaaaa"
                ref={startDateRef}
            />
        ),
        end_date: (
            <>
                <input 
                    type="text" 
                    value={formData['end_date'] || ''} 
                    className='shadow appearance-none border border-gray-300 rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline' 
                    name="end_date" 
                    onChange={(e) => handleDateChange('end_date', e)} 
                    placeholder="ggmmaaaa"
                    ref={endDateRef}
                />
                <div className="col-span-3 flex items-center text-end">
                    <p className='text-xs w-full'>*Non effettuando la selezione della Data Fine il contratto verrà riconosciuto come <span className='underline'>Indeterminato</span></p>
                </div>
            </>
        ),
        shift_type: <ListBox
            options={[
                { name: 'Fisso', value: 'fisso' },
                { name: 'Turno', value: 'turno' },
                { name: 'Senza Calendario', value: 'senza_calendario' },
                { name: 'A Chiamata', value: 'a_chiamata' }
            ]}
            defaultValue={formData['shift_type'] ? 
                { name: getShiftTypeDescriptionByValue(formData['shift_type']), value: formData['shift_type'] } : undefined}
            onChange={(selectedOption) => {
                onListChangeHandler('shift_type', selectedOption.value);
            }}
            required
        />,
        week_hours: <input 
            value={formData['week_hours'] || ''} 
            type="number" 
            className='shadow appearance-none border border-gray-300 rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline' 
            name="week_hours" 
            step="0.01"
            onChange={(e) => onChangeHandler('week_hours', e)} 
            ref={weekHoursRef}
            min="0"
        />,
        typology: (
            <div className='border border-gray-300 rounded-md'>
                <ComboboxAutocompleteAPI
                    getData={(query) => getContractTypesList(token, query)}
                    getDisplay={(typology) => `${typology?.code} - ${typology?.description}`}
                    getId={(typology) => typology?.id}
                    onChange={(id) => handleComboBoxChange('typology', id)}
                    currentValue={formData['typology']}
                />
            </div>
        ),
        ccnl: (
            <div className='border border-gray-300 rounded-md'>
                <ComboboxAutocompleteAPI
                    getData={(query) => getCCNLList(token, query)}
                    getDisplay={(ccnl) => `${ccnl?.codice} - ${ccnl?.nome}`}
                    getId={(ccnl) => ccnl?.id}
                    onChange={(selectedCcnl) => {
                        handleComboBoxChange('ccnl', selectedCcnl);
                        setSelectedCcnlId(selectedCcnl?.id || null);
                    }}
                    currentValue={formData['ccnl']}
                />
            </div>
        ),        
        classification_level: (
            <div className='border border-gray-300 rounded-md'>
                <ComboboxAutocompleteAPI
                    getData={(query) => {
                        if (selectedCcnlId === null)
                            return;
                        
                        return getClassificationLevelList(token, selectedCcnlId, query)
                    }}
                    getDisplay={(level) => {
                        return `${level?.cod_livello} - ${level?.des_livello}`;
                    }}
                    getId={(level) => level?.id}
                    onChange={(selectedLevel) => handleComboBoxChange('classification_level', selectedLevel)}
                    currentValue={formData['classification_level']}
                />
            </div>
        ),
        qualification:  <div className='border border-gray-300 rounded-md'>
            <ComboboxAutocompleteAPI
                getData={(query) => getWorkTaskList(token, query)}
                getDisplay={(qualification) => `${qualification?.codice} - ${qualification?.descrizione}`}
                getId={(qualification) => qualification?.id}
                onChange={(id) => handleComboBoxChange('qualification', id)}
                currentValue={formData['qualification']}
            />
        </div>,
        working_hours_type:  <div className='border border-gray-300 rounded-md'>
            <ComboboxAutocompleteAPI
                getData={(query) => getWorkingHourTypesList(token, query)}
                getDisplay={(workingHour) => `${workingHour?.codice} - ${workingHour?.nome}`}
                getId={(workingHour) => workingHour?.id}
                onChange={(id) => handleComboBoxChange('working_hours_type', id)}
                currentValue={formData['working_hours_type']}
            />
        </div>,
        ral: <>  
            <input
                value={formData['ral'] || ''}
                required
                type="text"
                className='shadow appearance-none border border-gray-300 rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline'
                name="ral"
                onChange={(e) => handleChange('ral', e)}
                placeholder='00.00'
            />
            {fieldErrors.ral && <span className="text-red-500">{fieldErrors.ral}</span>}
        </>,
        social_security_institution: (
            <div className='border border-gray-300 rounded-md'>
                <ComboboxAutocompleteAPI
                    getData={(query) => getEntePrevidenzialeList(token, query)}
                    getDisplay={(ente) => `${ente?.code} - ${ente?.description}`}
                    getId={(ente) => ente?.id}
                    onChange={(id) => handleComboBoxChange('social_security_institution', id)}
                    currentValue={formData['social_security_institution']}
                />
            </div>
        ),
        pat_inail: <>
            <input 
                type="text" 
                value={formData['pat_inail'] || ''} 
                className='shadow appearance-none border border-gray-300 rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline' 
                name="pat_inail"
                minLength={8}
                maxLength={10} 
                onChange={(e) => handleChange('pat_inail', e)}
                ref={patInailRef} 
            />
            {fieldErrors.ral && <span className="text-red-500">{fieldErrors.ral}</span>}
        </>,
        is_working_partner: (
            <ListBox
                options={[
                    { name: '-', value: null },
                    { name: 'SI', value: true },
                    { name: 'NO', value: false },
                ]}
                defaultValue={
                    formData['is_working_partner'] === null
                        ? { name: '-', value: null }
                        : {
                            name: formData['is_working_partner'] === true ? 'SI' : 'NO',
                            value: formData['is_working_partner'],
                        }
                }
                onChange={(selectedOption) => {
                    onListChangeHandler('is_working_partner', selectedOption.value);
                }}
            />
        ),
        is_mobile_worker: (
            <ListBox
                options={[
                    { name: '-', value: null },
                    { name: 'SI', value: true },
                    { name: 'NO', value: false },
                ]}
                defaultValue={
                    formData['is_mobile_worker'] !== null
                        ? {
                            name: formData['is_mobile_worker'] === true ? 'SI' : 'NO',
                            value: formData['is_mobile_worker'],
                        }
                        : { name: '-', value: null }
                }
                onChange={(selectedOption) => {
                    onListChangeHandler('is_mobile_worker', selectedOption.value);
                }}
            />
        ),

        is_seasonal_work: <ListBox
            options={[
                { name: '-', value: null },
                { name: 'SI', value: true },
                { name: 'NO', value: false },
            ]}
            defaultValue={
                formData['is_seasonal_work'] !== null
                    ? {
                        name: formData['is_seasonal_work'] === true ? 'SI' : 'NO',
                        value: formData['is_seasonal_work'],
                    }
                    : { name: '-', value: null }
            }
            onChange={(selectedOption) => {
                onListChangeHandler('is_seasonal_work', selectedOption.value);
            }}
        />,
        is_agriculture: (
            <ListBox
                options={[
                    { name: '-', value: null },
                    { name: 'SI', value: true },
                    { name: 'NO', value: false },
                ]}
                defaultValue={
                    formData['is_agriculture'] !== null
                        ? {
                            name: formData['is_agriculture'] === true ? 'SI' : 'NO',
                            value: formData['is_agriculture'],
                        }
                        : { name: '-', value: null }
                }
                onChange={(selectedOption) => {
                    onListChangeHandler('is_agriculture', selectedOption.value);
                }}
            />
        ),
    };

    return <>
        <form className='m-8'>
            {
                Object.keys(fields).map((fieldNameKey) => {
                    const fieldName = fieldNameKey;
                    const field = fields[fieldName];
                    const isRequired = requiredFields.includes(fieldName);
                    
                    return <div key={fieldName}>
                        <div className='grid grid-cols-3 gap-3'>
                            <div className='col-span-1 items-center flex mb-3'>
                                <label className="font-semibold text-sm text-gray-700">
                                    {labels[fieldName]} {isRequired && ' *'}
                                </label>
                            </div>
                            <div className='col-span-2 mb-3'>
                                {field}
                                {fieldErrors[fieldName] && (
                                    <div className="col-span-3 flex items-center text-center">
                                        <p className='text-xs text-red-500 font-semibold ml-1'>{fieldErrors[fieldName]}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                })
            }
        </form>

        <div className='col-span-3 my-4 mx-8 flex justify-center'>
            <button
                className={`font-bold py-2 px-4 rounded-md text-white text-sm mr-4 w-24 ${
                    isSaving ? 'bg-gray-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-500 text-white  focus:bg-indigo-600 focus:outline-none focus:shadow-outline'
                  }`} 
                onClick={saveHandler}
                disabled={isSaving}
            >
                {isSaving ? 'Salvataggio...' : 'Salva'}
            </button>
            { 
                undoCallback && 
                <button 
                className='border block rounded-md bg-grey-400 px-3 py-2 mr-2 text-center text-sm font-semibold text-grey-800 shadow-sm hover:bg-grey-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-grey-600 w-24'
                onClick={() => {
                    undoCallback();
                    setUnsavedChanges(false);
                }}
            >
                Annulla
            </button>
            }
        </div>
    </>
}


export default function ContractTabEmployee({employeeId, setUnsavedChanges}) {
    const { token } = useContext(AuthContext)
    const [contracts, setContracts] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [inEdit, setInEdit] = useState(null);

    useEffect(() => {
        if(!employeeId) {
            return;
        }

       getContractList();

    }, [employeeId]);

    const getContractList = function () {
        getEmployeeContractsList(token, employeeId, 'payrolls').then(data => {
            setContracts(data || []);
        });
    }

    const formatters = {
        start_date: (value) => {
            if(!value) {
                return '';
            }
            
            return new Date(value).toLocaleDateString('it');
        },
        end_date: (value) => {
            if(!value) {
                return '';
            }
            
            return new Date(value).toLocaleDateString('it');
        },
        typology: (value)  => {
            if(!value) {
                return '';
            }
            
            return (value.description);
        },
        ccnl: (value) => value ? `${value?.codice} - ${value?.nome}` : '',
        classification_level: (value) => value ? `${value?.cod_livello} - ${value?.des_livello}` : '',
        qualification: (value) => value?.descrizione,
        social_security_institution: (value) => value?.description,
        shift_type: (value) => getShiftTypeDescriptionByValue(value),
        working_hours_type: (value) => value ? `${value?.codice} - ${value?.nome}` : '',
        is_working_partner: (value) => value === null || value === undefined ? '' : value ? 'SI' : 'NO',
        is_mobile_worker: (value) => value === null || value === undefined ? '' : value ? 'SI' : 'NO',
        is_seasonal_work: (value) => value === null || value === undefined ? '' : value ? 'SI' : 'NO',
        is_agriculture: (value) => value === null || value === undefined ? '' : value ? 'SI' : 'NO',
        is_mandatory_hiring: (value) => value === null || value === undefined ? '' : value ? 'SI' : 'NO', 
    };

    useEffect(() => {
        const queryParams = new URLSearchParams(window.location.search);
        if (queryParams.get('showForm') === 'true') {
          setShowForm(true);
          queryParams.delete('showForm');
          const newUrl = `${window.location.pathname}?${queryParams.toString()}`;
          window.history.replaceState(null, '', newUrl);
        }
    }, []);
    
    const handleShowForm = () => {
        setShowForm(prevShowForm => !prevShowForm);
        setUnsavedChanges(false)
    };

    const onCreateSuccess = () => {
        setShowForm(false);
        getContractList();
        setUnsavedChanges(false);
    };

    const onUpdateSuccess = () => {
        setInEdit(null);
        getContractList();
        setUnsavedChanges(false);
    };

    const undoCallback = () => {
        setInEdit(null);
        getContractList();
        setUnsavedChanges(false);
    };


    const handleDeleteContract = async (contractId) => {
        try {
            const result = await Swal.fire({
                title: "Sei sicuro di voler eliminare il contratto?",
                showDenyButton: true,
                confirmButtonText: "Sì, elimina",
                denyButtonText: "Annulla",
                denyButtonColor: "#d6d3d1",
            });
    
            if (result.isConfirmed) {
                Swal.fire({
                    title: 'Eliminazione in corso...',
                    didOpen: () => {
                        Swal.showLoading();
                    },
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                    allowEnterKey: false,
                });
    
                await deleteEmployeeContract(token, employeeId, contractId);
    
                Swal.fire({
                    title: "Contratto eliminato con successo!",
                    icon: "success",
                });
    
                const updatedContracts = await getEmployeeContractsList(token, employeeId);
                setContracts(updatedContracts);
            } else if (result.isDenied) {
                return;
            }
        } catch (error) {
            const errorMessage = estraiMessaggiErrore(error);
            Swal.fire({
                icon: 'error',
                title: 'Errore',
                text: errorMessage,
            });
        }
    };

    return <>
        {
            inEdit === null &&
            <div className='flex justify-end my-4 me-5'>
                <button 
                    className="border border-gray-400 rounded-md bg-grey-400 me-1 px-3 py-2 text-center text-sm font-semibold text-grey-800 shadow-md hover:bg-grey-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-grey-600 w-60"
                    onClick={handleShowForm}
                >
                    {showForm ? <><ArrowUturnLeftIcon className="h-4 w-4 inline-block mr-1" /> Annulla</> : <><PlusIcon className="h-4 w-4 inline-block mr-2" /> Aggiungi Nuovo Contratto</>}
                </button>
            </div>
        }
        {showForm && (
            <ContractForm employeeId={employeeId} successCallback={onCreateSuccess} setUnsavedChanges={setUnsavedChanges} />
        )}

        {/* VISUALIZZAZIONE DELLA TAB CONTRATTO ALL'INTERNO DI MODIFICA DIPENDENTE */}
        <div className='container px-4 pb-4 w-full' style={{ minHeight: '80vh', height: 'auto' }}> 
            {
                contracts
                .sort((a, b) => new Date(b.start_date).getTime() - new Date(a.start_date).getTime())
                .map(contract => 
                    <div key={contract.id} className='rounded px-2 sm:px-8 pt-3 mb-4 m-6 border border-gray-400 shadow-sm p-4'>
                        {
                            inEdit === contract.id
                            ? <ContractForm employeeId={employeeId} successCallback={onUpdateSuccess} undoCallback={undoCallback} initialValues={contract} setUnsavedChanges={setUnsavedChanges} />
                            :
                            <>
                                <div className="flex items-center pb-1">
                                    <div className="grid grid-cols-2 justify-between w-full">
                                        <div className='col-span-3 h-2'></div>
                                        <div className="col-span-1 pb-2">
                                            {contract.is_active ? <CheckCircleIcon className="inline-block w-5 h-5 text-indigo-700" /> : <XCircleIcon className="inline-block w-5 h-5 text-gray-900" />}
                                            <h1 className={`inline-block ml-1 ${contract.is_active ? 'text-indigo-700' : 'text-gray-900'} font-semibold uppercase`}>
                                                {contract.is_active ? 'Attivo' : 'Non Attivo'}
                                            </h1>
                                        </div>
                                        <div className="col-span-1 flex justify-end">
                                            {!showForm && (
                                                <>
                                                    <button 
                                                        className="ml-2 inline-flex items-center rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                                                        onClick={() => setInEdit(contract.id)}
                                                    >
                                                        <PencilIcon className="h-4 w-4 inline-block mr-2" />
                                                        Modifica
                                                    </button>
                                                    <button 
                                                        className="ml-2 inline-flex items-center rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 hover:text-red-500"
                                                        onClick={() => handleDeleteContract(contract.id.toString())}
                                                    >
                                                        <TrashIcon className="h-4 w-4 inline-block mr-2" />
                                                        Elimina
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className='h-6 flex sm:hidden'></div>

                                {
                                    Object.keys(labels).map((contractProperty) => {
                                        const prop = contractProperty;
                                        const formatter = formatters[prop] ? formatters[prop] : (value) => value;

                                        const value = formatter(contract[prop]);
                                        return <div key={`${contract.id}-${contractProperty}`}>
                                            <div className='grid grid-cols-3 gap-3'>
                                                <div className='col-span-1 items-center flex pb-2'>
                                                    <label className="font-semibold text-sm text-gray-700">
                                                        {labels[prop]}
                                                    </label>
                                                </div>
                                                <div className='col-span-2 font-semibold text-sm text-gray-700'>
                                                    {value}
                                                </div>
                                            </div>
                                        </div>
                                    })
                                }
                            </>
                        }
                    </div>
                )
            }
        </div>
    </>;
};

